import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import {CheckCircle, Share2, UserPlus, Facebook, MessageCircleDashed as MessageCircle, Copy, Download, Image as ImageIcon, Instagram, Twitter, Linkedin, Mail} from 'lucide-react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'
import { generateShareCard } from '../utils/shareCardGenerator'
import { generateWhatsAppMessage, generateFacebookMessage, generateInstagramMessage, generateTwitterMessage, generateLinkedInMessage } from '../utils/shareTemplates'

export function ThankYou() {
  const { requestId } = useParams()
  const navigate = useNavigate()
  const [request, setRequest] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [shareCardUrl, setShareCardUrl] = useState<string | null>(null)
  const [generatingCard, setGeneratingCard] = useState(false)

  useEffect(() => {
    const loadRequest = async () => {
      if (!requestId) return
      try {
        const data = await lumi.entities.requests.get(requestId)
        setRequest(data)
        // Auto-generate card after loading request
        await autoGenerateCard(data)
      } catch (error) {
        console.error('Error loading request:', error)
        toast.error('Erro ao carregar solicitação')
      } finally {
        setLoading(false)
      }
    }
    loadRequest()
  }, [requestId])

  const autoGenerateCard = async (requestData: any) => {
    if (!requestData) return
    console.log('🎨 Auto-generating card on page load...')
    setGeneratingCard(true)
    try {
      const cardUrl = await generateShareCard({
        patientName: requestData.patientName,
        bloodType: requestData.bloodType,
        hospital: requestData.hospital,
        city: requestData.city,
        state: requestData.state,
        urgency: requestData.urgency,
        unitsNeeded: requestData.unitsNeeded,
        patientPhoto: requestData.patientPhoto
      })
      console.log('✅ Card auto-generated:', cardUrl)
      setShareCardUrl(cardUrl)
    } catch (error) {
      console.error('⚠️ Error auto-generating card:', error)
    } finally {
      setGeneratingCard(false)
    }
  }

  const shareUrl = `${window.location.origin}/solicitacoes`
  const shareText = request 
    ? `🩸 URGENTE: ${request.patientName} precisa de doação de sangue tipo ${request.bloodType} em ${request.city}/${request.state}. Ajude compartilhando!`
    : 'Ajude salvando vidas através de doação de sangue'

  const handleShareWhatsApp = () => {
    const message = request ? generateWhatsAppMessage({
      bloodType: request.bloodType,
      patientName: request.patientName,
      hospital: request.hospital,
      city: request.city,
      state: request.state,
      urgency: request.urgency,
      shareToken: request._id
    }) : shareText
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`
    window.open(url, '_blank')
  }

  const handleShareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`
    window.open(url, '_blank')
  }

  const handleShareInstagram = () => {
    if (!shareCardUrl) {
      toast.error('Gere o cartão visual primeiro!')
      return
    }
    toast.success('📸 Baixe a imagem e compartilhe no Instagram Stories!')
    handleDownloadCard()
  }

  const handleShareTwitter = () => {
    const message = request ? generateTwitterMessage({
      bloodType: request.bloodType,
      patientName: request.patientName,
      hospital: request.hospital,
      city: request.city,
      state: request.state,
      urgency: request.urgency,
      shareToken: request._id
    }) : shareText
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`
    window.open(url, '_blank')
  }

  const handleShareLinkedIn = () => {
    const message = request ? generateLinkedInMessage({
      bloodType: request.bloodType,
      patientName: request.patientName,
      hospital: request.hospital,
      city: request.city,
      state: request.state,
      urgency: request.urgency,
      shareToken: request._id
    }) : shareText
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    window.open(url, '_blank')
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl)
    toast.success('Link copiado!')
  }

  const handleGenerateCard = async () => {
    if (!request) {
      console.error('❌ No request data available')
      toast.error('Dados da solicitação não encontrados')
      return
    }
    
    console.log('🚀 Starting card generation for request:', request)
    setGeneratingCard(true)
    
    try {
      const cardUrl = await generateShareCard({
        patientName: request.patientName,
        bloodType: request.bloodType,
        hospital: request.hospital,
        city: request.city,
        state: request.state,
        urgency: request.urgency,
        unitsNeeded: request.unitsNeeded,
        patientPhoto: request.patientPhoto
      })
      console.log('✅ Card URL received:', cardUrl)
      setShareCardUrl(cardUrl)
      toast.success('🎨 Cartão visual gerado!')
    } catch (error) {
      console.error('❌ Error generating card:', error)
      toast.error(`Erro ao gerar cartão: ${error instanceof Error ? error.message : 'Erro desconhecido'}`)
    } finally {
      setGeneratingCard(false)
    }
  }

  const handleDownloadCard = () => {
    if (!shareCardUrl) return
    const link = document.createElement('a')
    link.href = shareCardUrl
    link.download = `doe-${request?.bloodType || 'doacao'}.png`
    link.click()
    toast.success('📥 Baixando cartão...')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-12 max-w-2xl">
        <Card className="border border-red-200 shadow-lg">
          <CardHeader className="text-center pb-6">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-red-600 flex items-center justify-center shadow-md">
                  <CheckCircle className="h-12 w-12 text-white" strokeWidth={3} />
                </div>
              </div>
            </div>
            
            <CardTitle className="text-4xl font-black text-gray-900 mb-3">
              Solicitação Criada!
            </CardTitle>
            <p className="text-lg text-gray-700 font-medium">
              Sua solicitação foi publicada com sucesso
            </p>
            {request?.contactEmail && (
              <p className="text-sm text-gray-600 mt-2">
                ✉️ Enviamos um e-mail de confirmação para <strong>{request.contactEmail}</strong>
              </p>
            )}
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Request Summary */}
            {request && (
              <div className="bg-white rounded-lg p-6 border border-red-200">
                <h3 className="font-bold text-lg text-gray-900 mb-3">📋 Resumo da Solicitação</h3>
                <div className="space-y-2 text-sm">
                  <p><span className="font-semibold">Paciente:</span> {request.patientName}</p>
                  <p><span className="font-semibold">Tipo Sanguíneo:</span> <span className="text-red-600 font-bold text-lg">{request.bloodType}</span></p>
                  <p><span className="font-semibold">Local:</span> {request.hospital}, {request.city}/{request.state}</p>
                  <p><span className="font-semibold">Unidades:</span> {request.unitsNeeded}</p>
                </div>
              </div>
            )}

            {/* Visual Card Section */}
            <div className="bg-gradient-to-br from-red-50 to-white border-2 border-red-300 rounded-xl p-6 shadow-lg">
              <div className="flex items-center space-x-3 mb-4">
                <ImageIcon className="h-7 w-7 text-red-600" />
                <h3 className="font-bold text-xl text-gray-900">🎨 Seu Cartão Visual</h3>
              </div>
              <p className="text-sm text-gray-700 mb-4">
                Banner formato Instagram Reels (9:16) pronto para compartilhar!
              </p>
              
              {generatingCard ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 font-semibold">🎨 Gerando seu cartão visual...</p>
                </div>
              ) : shareCardUrl ? (
                <div className="space-y-4">
                  <div className="relative group">
                    <img 
                      src={shareCardUrl} 
                      alt="Cartão de doação" 
                      className="w-full rounded-xl border-4 border-red-200 shadow-2xl transition-transform group-hover:scale-[1.02]"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 rounded-xl transition-colors"></div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={handleDownloadCard}
                      className="bg-red-600 hover:bg-red-700 h-12 font-bold"
                    >
                      <Download className="h-5 w-5 mr-2" />
                      Baixar PNG
                    </Button>
                    <Button
                      onClick={handleGenerateCard}
                      variant="outline"
                      className="h-12 border-2 border-red-300 font-semibold"
                    >
                      🔄 Gerar Novo
                    </Button>
                  </div>
                  
                  {/* Share with Card */}
                  <div className="bg-white rounded-lg p-4 border-2 border-red-200">
                    <p className="text-sm font-bold text-gray-900 mb-3">📤 Compartilhar com a Imagem:</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        onClick={handleShareInstagram}
                        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 h-11 font-bold text-sm"
                      >
                        <Instagram className="h-4 w-4 mr-2" />
                        Instagram
                      </Button>
                      <Button
                        onClick={handleShareWhatsApp}
                        className="bg-green-600 hover:bg-green-700 h-11 font-bold text-sm"
                      >
                        <MessageCircle className="h-4 w-4 mr-2" />
                        WhatsApp
                      </Button>
                      <Button
                        onClick={handleShareFacebook}
                        className="bg-blue-600 hover:bg-blue-700 h-11 font-bold text-sm"
                      >
                        <Facebook className="h-4 w-4 mr-2" />
                        Facebook
                      </Button>
                      <Button
                        onClick={handleShareTwitter}
                        className="bg-sky-500 hover:bg-sky-600 h-11 font-bold text-sm"
                      >
                        <Twitter className="h-4 w-4 mr-2" />
                        Twitter
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">Erro ao gerar cartão automaticamente</p>
                  <Button
                    onClick={handleGenerateCard}
                    className="bg-red-600 hover:bg-red-700 h-12 font-bold"
                  >
                    <ImageIcon className="h-5 w-5 mr-2" />
                    Tentar Novamente
                  </Button>
                </div>
              )}
            </div>

            {/* Additional Share Options */}
            <div className="bg-white border border-red-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Share2 className="h-6 w-6 text-red-600" />
                <h3 className="font-bold text-lg text-gray-900">Mais Opções de Compartilhamento</h3>
              </div>
              <p className="text-sm text-gray-700 mb-4">
                Quanto mais pessoas virem, mais rápido conseguiremos um doador!
              </p>
              
              <div className="space-y-3">
                <Button
                  onClick={handleShareLinkedIn}
                  className="w-full bg-blue-700 hover:bg-blue-800 h-12 font-bold text-base"
                >
                  <Linkedin className="h-5 w-5 mr-2" />
                  Compartilhar no LinkedIn
                </Button>
                
                <Button
                  onClick={handleCopyLink}
                  variant="outline"
                  className="w-full border-2 border-gray-300 h-12 font-semibold"
                >
                  <Copy className="h-5 w-5 mr-2" />
                  Copiar Link da Solicitação
                </Button>
              </div>
            </div>

            {/* Create Account CTA */}
            {request?.isAnonymous && (
              <div className="bg-white border border-red-200 rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <UserPlus className="h-6 w-6 text-red-600" />
                  <h3 className="font-bold text-lg text-gray-900">Crie sua conta grátis</h3>
                </div>
                <p className="text-sm text-gray-700 mb-4">
                  Acompanhe sua solicitação, receba notificações quando doadores entrarem em contato e ajude outras pessoas também.
                </p>
                <Button
                  onClick={() => navigate('/sou-doador')}
                  className="w-full bg-red-600 hover:bg-red-700 text-white h-12 font-bold"
                >
                  Criar Conta Gratuita
                </Button>
              </div>
            )}

            {/* Next Steps */}
            <div className="bg-white rounded-lg p-5 border border-red-200">
              <h3 className="font-bold text-gray-900 mb-3">📍 Próximos Passos</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-600 font-bold mr-2">✓</span>
                  <span>Sua solicitação está visível para todos os doadores cadastrados</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 font-bold mr-2">→</span>
                  <span>Compartilhe nas redes sociais para alcançar mais pessoas</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-600 font-bold mr-2">📧</span>
                  <span>Doadores entrarão em contato pelos meios informados</span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                onClick={() => navigate('/solicitacoes')}
                variant="outline"
                className="flex-1 h-12 font-semibold border-2"
              >
                Ver Todas as Solicitações
              </Button>
              <Button
                onClick={() => navigate('/')}
                className="flex-1 h-12 font-bold bg-red-600 hover:bg-red-700"
              >
                Voltar ao Início
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
